<template>
    <main
        ref="scrollContainer"
        class="relative min-h-screen w-screen overflow-y-auto"
    >
        <div class="absolute top-4 right-4 z-50">
            <button
                class="text-sm text-theme-muted hover:text-theme-base transition flex items-center gap-1"
                @click="onSignOut"
            >
                Cerrar sesión
            </button>
        </div>
        <div class="flex flex-col items-center min-h-screen w-full py-10 px-4">
            <div
                v-if="stepKey"
                class="w-full mx-auto text-center"
                :class="containerWidthClass"
            >
                <h1 class="text-3xl font-bold text-theme-base">
                    {{ title }}
                </h1>
                <p v-if="subtitle" class="text-theme-muted mt-2">
                    {{ subtitle }}
                </p>
            </div>

            <div
                v-if="stepKey"
                class="w-full mx-auto mt-4"
                :class="containerWidthClass"
            >
                <onboarding-step-dots
                    :current-step="stepKey"
                    @navigate="goToStep"
                />
            </div>

            <div
                v-if="hasPreviewPanel"
                class="w-full mx-auto mt-4"
                :class="containerWidthClass"
            >
                <div class="mb-4 flex gap-2 lg:hidden">
                    <button
                        type="button"
                        class="flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition"
                        :class="
                            mobileTab === 'form'
                                ? 'border-theme-primary bg-theme-hover text-theme-primary'
                                : 'border-theme-border text-theme-muted'
                        "
                        @click="mobileTab = 'form'"
                    >
                        Formulario
                    </button>
                    <button
                        type="button"
                        class="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition"
                        :class="
                            mobileTab === 'preview'
                                ? 'border-theme-primary bg-theme-hover text-theme-primary'
                                : 'border-theme-border text-theme-muted'
                        "
                        @click="mobileTab = 'preview'"
                    >
                        <eye-icon class="h-4 w-4" />
                        Vista previa
                    </button>
                </div>

                <div class="grid gap-8 lg:grid-cols-2 lg:items-start">
                    <!-- min-w-0: sin esto un texto largo (una URL) ensancha la
                         columna y toda la pantalla en celular. -->
                    <div
                        class="min-w-0"
                        :class="
                            mobileTab === 'form' ? 'block' : 'hidden lg:block'
                        "
                    >
                        <router-view />
                    </div>
                    <div
                        class="flex min-w-0 flex-col items-center gap-3 lg:sticky lg:top-8"
                        :class="
                            mobileTab === 'preview' ? 'flex' : 'hidden lg:flex'
                        "
                    >
                        <p
                            class="rounded-lg bg-theme-warning-soft px-3 py-2 text-center text-xs font-medium text-theme-warning-strong"
                        >
                            Algunos de los datos se han puesto valores por
                            defecto para ser más ilustrativos.
                        </p>
                        <catalog-preview-frame
                            :catalog="previewCatalog"
                            class="overflow-hidden rounded-2xl shadow-lg"
                        />
                    </div>
                </div>
            </div>

            <div
                v-else
                class="w-full mx-auto mt-4"
                :class="containerWidthClass"
            >
                <router-view />
            </div>

            <div
                v-if="stepKey"
                class="w-full mx-auto mt-10"
                :class="containerWidthClass"
            >
                <onboarding-stepper
                    :current-step="stepKey"
                    :locked="readOnly"
                    :next-disabled="stepperUi.nextDisabled"
                    :next-label="stepperUi.nextLabel"
                    :next-variant="stepperUi.nextVariant"
                    :loading="stepperUi.loading"
                    @navigate="goToStep"
                    @back="goBack"
                    @next="requestNext"
                />
            </div>
        </div>
        <plan-upgrade-host />
        <onboarding-exit-guard
            ref="exitGuard"
            :active="exitGuardActive"
            :step="analyticsStep"
            :remaining-steps="stepsUntilPlan"
        />
    </main>
</template>

<script>
import onboardingStepNavigation from "@/mixins/onboardingStepNavigation";
import { mapActions } from "vuex";

import CatalogPreviewFrame from "@/components/editor/CatalogPreviewFrame.vue";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import PlanUpgradeHost from "@/components/layout/PlanUpgradeHost.vue";
import OnboardingExitGuard from "@/components/onboarding/OnboardingExitGuard.vue";
import OnboardingStepDots from "@/components/onboarding/OnboardingStepDots.vue";
import OnboardingStepper from "@/components/onboarding/OnboardingStepper.vue";

// Steps que muestran el panel de vista previa del catalogo junto al
// formulario. `slug`, `business-type` y `plan` se quedan con el layout de
// una sola columna (nunca tuvieron preview).
const PREVIEW_PANEL_STEPS = [
    "branding",
    "social-networks",
    "custom-links",
    "products"
];

// Steps que necesitan mas ancho que el resto: los de preview (form + panel
// lado a lado) y `plan` (grid de hasta 3 planes pagos, se ve apretado en
// max-w-4xl aunque no tenga preview).
const WIDE_CONTAINER_STEPS = [...PREVIEW_PANEL_STEPS, "plan"];

const STEP_TITLES = {
    slug: "Dale nombre a tu catálogo",
    "business-type": "¿Qué tipo de negocio tienes?",
    branding: "Personaliza tu marca",
    "social-networks": "¿En qué plataformas estás?",
    products: "Agrega tus productos",
    "custom-links": "Agrega tus enlaces",
    plan: "Elige tu plan"
};

const STEP_SUBTITLES = {
    slug: "Escribe el nombre de tu negocio o perfil y te sugerimos una URL para tu catálogo",
    "business-type": "Esto nos ayuda a personalizar tu experiencia",
    branding: "Agrega tu logo y elige los colores que representan tu negocio",
    "social-networks": "Selecciona las redes donde tienes presencia",
    products: "Crea los productos de tu catálogo. Puedes agregar más después.",
    "custom-links": "Agrega enlaces personalizados a tu catálogo (opcional)",
    plan: "Empieza gratis, prueba Pro o paga el plan que necesitas. Puedes cambiarlo después."
};

// Nombre del step en los eventos de analítica cuando difiere de la clave
// interna (ruta/backend).
const ANALYTICS_STEP_NAMES = {
    "custom-links": "links"
};

// Orden fijo del flujo completo (incluye pasos que los tipos sin productos
// omite), usado solo para numerar el evento de analítica de forma estable
// sin depender de que orderedSteps ya haya cargado el flow del backend.
const STEP_SEQUENCE = [
    "slug",
    "business-type",
    "branding",
    "social-networks",
    "custom-links",
    "plan",
    "products"
];

export default {
    name: "LayoutOnboardingMultitenant",

    components: {
        CatalogPreviewFrame,
        EyeIcon,
        OnboardingExitGuard,
        OnboardingStepDots,
        OnboardingStepper,
        PlanUpgradeHost
    },

    mixins: [onboardingStepNavigation],

    data() {
        return {
            mobileTab: "form"
        };
    },

    computed: {
        stepKey() {
            return this.$route.meta.stepKey;
        },
        title() {
            return STEP_TITLES[this.stepKey] || "";
        },
        subtitle() {
            return STEP_SUBTITLES[this.stepKey] || "";
        },
        hasPreviewPanel() {
            return PREVIEW_PANEL_STEPS.includes(this.stepKey);
        },
        containerWidthClass() {
            return WIDE_CONTAINER_STEPS.includes(this.stepKey)
                ? "max-w-6xl"
                : "max-w-4xl";
        },
        analyticsStep() {
            if (!this.stepKey) return "";
            return ANALYTICS_STEP_NAMES[this.stepKey] || this.stepKey;
        },
        exitGuardActive() {
            return Boolean(this.stepKey) && !this.isStepCompleted("plan");
        },
        stepsUntilPlan() {
            const planIndex = this.orderedSteps.indexOf("plan");
            const currentIndex = this.orderedSteps.indexOf(this.stepKey);
            if (planIndex === -1 || currentIndex === -1) return 0;
            return Math.max(planIndex - currentIndex, 0);
        }
    },

    watch: {
        stepKey: {
            immediate: true,
            handler(step) {
                if (!step) return;
                this.$analytics.trackEvent("onboarding_step_viewed", {
                    onboarding_type: "multitenant",
                    step: ANALYTICS_STEP_NAMES[step] || step,
                    step_number: STEP_SEQUENCE.indexOf(step) + 1
                });
            }
        },

        // El botón "Siguiente" vive en este layout persistente (no se
        // destruye entre steps), así que el navegador puede mantenerle el
        // foco y volver a scrollear hacia él tras el cambio de ruta. Se
        // quita el foco y se fuerza el reset en nextTick para ganarle a
        // ese re-scroll.
        "$route.path"() {
            this.mobileTab = "form";
            this.$nextTick(() => {
                if (
                    document.activeElement &&
                    document.activeElement !== document.body
                ) {
                    document.activeElement.blur();
                }
                if (this.$refs.scrollContainer) {
                    this.$refs.scrollContainer.scrollTop = 0;
                }
                window.scrollTo(0, 0);
            });
        }
    },

    methods: {
        ...mapActions("auth", ["signOut"]),

        onSignOut() {
            this.$refs.exitGuard.release();
            this.signOut();
        }
    }
};
</script>
